import { Link } from "@inertiajs/react";
import AppShell from "../../components/Shell/AppShell";
import { pick } from "../../lib/i18n";
import type { BrandSummary, CurrentUser, ProductSummary } from "../../types";

const copy = {
  pl: { title: "Marki", subtitle: "Zarządzaj markami i ich logo używanymi w generacji grafik.", newBrand: "+ Nowa marka", noBrands: "Brak marek", noBrandsHint: "Utwórz pierwszą markę, żeby dodać jej logo do generacji grafik.", inactive: "nieaktywna" },
  en: { title: "Brands", subtitle: "Manage brands and their logos used in visual generation.", newBrand: "+ New brand", noBrands: "No brands", noBrandsHint: "Create your first brand to add its logo to visual generation.", inactive: "inactive" },
};

type Props = {
  brands: BrandSummary[];
  current_user: CurrentUser;
  products?: ProductSummary[];
};

export default function BrandsIndex({ brands, current_user, products = [] }: Props) {
  const initials = current_user.name.split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase();
  const canEdit  = current_user.role === "admin" || current_user.role === "moderator";
  const t = pick(copy);

  return (
    <AppShell products={products} user={{ name: current_user.name, initials, role: current_user.role }}>
      <div className="page">
        <div className="page-hd">
          <div>
            <h2>{t.title}</h2>
            <p>{t.subtitle}</p>
          </div>
          {canEdit && (
            <div className="right">
              <Link href="/brands/new" className="btn btn-primary btn-sm">{t.newBrand}</Link>
            </div>
          )}
        </div>

        {brands.length === 0 ? (
          <div className="empty">
            <h4>{t.noBrands}</h4>
            <p>{t.noBrandsHint}</p>
            {canEdit && (
              <Link href="/brands/new" className="btn btn-primary btn-sm" style={{ marginTop: 16 }}>
                {t.newBrand}
              </Link>
            )}
          </div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 10 }}>
            {brands.map((brand) => (
              <Link
                key={brand.id}
                href={`/brands/${brand.id}`}
                style={{ textDecoration: "none" }}
              >
                <div className="panel" style={{ cursor: "pointer", transition: "border-color .12s" }}>
                  <div
                    style={{
                      height: 96,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      background: "var(--bg-2)",
                      borderBottom: "1px solid var(--line)",
                    }}
                  >
                    {brand.first_logo_url ? (
                      <img
                        src={brand.first_logo_url}
                        alt={brand.name}
                        style={{ maxHeight: 72, maxWidth: "80%", objectFit: "contain" }}
                      />
                    ) : (
                      <span style={{ fontSize: 28, color: "var(--muted-2)", fontWeight: 700, letterSpacing: -1 }}>
                        {brand.name.slice(0, 2).toUpperCase()}
                      </span>
                    )}
                  </div>
                  <div className="panel-body" style={{ padding: "10px 12px" }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                      <span style={{ fontSize: 13, fontWeight: 600, color: "var(--ink)", letterSpacing: "-0.01em" }}>
                        {brand.name}
                      </span>
                      {!brand.active && (
                        <span
                          className="mono"
                          style={{ fontSize: 9.5, color: "var(--muted-2)", background: "var(--bg-3)", padding: "2px 5px", borderRadius: 2 }}
                        >
                          {t.inactive}
                        </span>
                      )}
                    </div>
                    <div className="mono" style={{ fontSize: 10.5, color: "var(--muted-2)", marginTop: 3 }}>
                      {brand.logos_count} {brand.logos_count === 1 ? "logo" : "logo"} · {brand.slug}
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </AppShell>
  );
}
